import { LevelStatus } from './LevelStatus';
import { CardValue, getCardSuit, getCardValue, Suit } from 'Utility/CardsHelper';
import { Quests, QuestType } from '../Player/QuestType';
import UIStateHelper from '../UI/UIStateHelper';
import LevelHelper from '../../Utility/LevelHelper';
import { ArrayHelper } from 'Utility/ArrayHelper';
import {
    levelCompleteRewardMultiplier,
    multiplierPerBoosterType,
    remainingStackRewardStartFactor,
    remainingStackRewardStartLinearFactor,
} from 'Constants/Economy';
import { AnyBoosterType, BoosterType, createAnyBoostersRecord, PreBoosterType } from '../Card/BoosterType';
import CardStateHelper from '../Card/CardStateHelper';
import { FBPixel } from 'Utility/SDK/FBPixelHandler';
import Game from 'Game';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import { WinCriteriaType } from 'Constants/WinCriteria';
import { LevelHelperSettings, lastFreePlusFiveBooster, STREAK_PROGRESS_STEPS } from 'Constants/Level';
import { LevelType } from '../Player/Progress/PlayerLevels';
import { Immutable, unexpectedValue } from 'Utility/Types';
import { ILevelState, ILevelStateTaskData } from './ILevelState';
import { IGameState } from '../Game/IGameState';
import { ICardsState, ICardStateData } from '../Card/ICardState';
import { INTERSTITIAL_FREQUENCY, INTERSTITIAL_START_LEVEL } from 'Constants/Ads';
import { CardType } from 'Prototype/Level/Card/CardType';
import { QuestStateHelper } from '../Player/Quests/QuestStateHelper';
import { ProgressStateHelper } from '../Player/Progress/ProgressStateHelper';
import { FTUX_LEVEL_TASKS } from 'Constants/FTUX';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { getFieldUnlockShareKey } from 'Constants/AvatarsAndCrops';
import { CardModifierType } from 'Prototype/Level/Card/CardModifierType';
import { LevelFailReason } from 'State/Game/GameActions';
import { track } from 'Utility/Tracking/Track';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { IRewardData } from 'Utility/RewardHelper';
import ContextStateHelper from 'State/Game/Context/ContextStateHelper';
import { ContextType } from 'State/Game/ContextType';
import { AdvertisementArea } from 'Utility/SDK/Advertisement';

const SUIT_QUEST_MAPPING: Record<Suit, QuestType> = {
    [Suit.CLUBS]: Quests.CLEAR_CARD_CLUBS,
    [Suit.HEARTS]: Quests.CLEAR_CARD_HEARTS,
    [Suit.SPADES]: Quests.CLEAR_CARD_SPADES,
    [Suit.DIAMONDS]: Quests.CLEAR_CARD_DIAMONDS,
};

const VALUE_QUEST_MAPPING: Record<CardValue, QuestType> = {
    [CardValue.ACE]: Quests.CLEAR_CARD_ACES,
    [CardValue.TWO]: Quests.CLEAR_CARD_2,
    [CardValue.THREE]: Quests.CLEAR_CARD_3,
    [CardValue.FOUR]: Quests.CLEAR_CARD_4,
    [CardValue.FIVE]: Quests.CLEAR_CARD_5,
    [CardValue.SIX]: Quests.CLEAR_CARD_6,
    [CardValue.SEVEN]: Quests.CLEAR_CARD_7,
    [CardValue.EIGHT]: Quests.CLEAR_CARD_8,
    [CardValue.NINE]: Quests.CLEAR_CARD_9,
    [CardValue.TEN]: Quests.CLEAR_CARD_10,
    [CardValue.JACK]: Quests.CLEAR_CARD_JACKS,
    [CardValue.QUEEN]: Quests.CLEAR_CARD_QUEENS,
    [CardValue.KING]: Quests.CLEAR_CARD_KINGS,
};

const LEVELS_TO_TRACK = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 150, 200];

export default class LevelStateHelper {
    public static updateQuestProgress(oldState: IGameState, previousState: IGameState): IGameState {
        let state = { ...oldState };

        // Get "cleared" cards (folded and discarded).
        const clearedCards = [].concat(
            state.levelState.cardsState.folded.filter(e => !previousState.levelState.cardsState.folded.includes(e)),
            state.levelState.cardsState.discarded.filter(
                e => !previousState.levelState.cardsState.discarded.includes(e)
            )
        );

        // Update clear cards quest progress.
        for (let i = 0; i < clearedCards.length; i++) {
            const cardData = state.levelState.cardsState.cards[clearedCards[i]];
            if (
                cardData.type === CardType.value &&
                previousState.levelState.cardsState.inPlay.includes(clearedCards[i])
            ) {
                state = {
                    ...state,
                    playerState: QuestStateHelper.incrementQuestProgressBy(
                        state.playerState,
                        SUIT_QUEST_MAPPING[getCardSuit(cardData.value)],
                        1
                    ),
                };
                state = {
                    ...state,
                    playerState: QuestStateHelper.incrementQuestProgressBy(
                        state.playerState,
                        VALUE_QUEST_MAPPING[getCardValue(cardData.value)],
                        1
                    ),
                };
                state = {
                    ...state,
                    playerState: QuestStateHelper.incrementQuestProgressBy(state.playerState, Quests.CLEAR_CARD_ANY, 1),
                };
            }
        }

        // Update streak quests progress.
        if (
            state.levelState.cardsState.matchSequence.length > 0 &&
            state.levelState.cardsState.matchSequence.length % STREAK_PROGRESS_STEPS === 0
        ) {
            state = {
                ...state,
                playerState: QuestStateHelper.incrementQuestProgressBy(state.playerState, Quests.COMBO_STREAK_BONUS, 1),
            };
        }

        return state;
    }

    public static levelComplete(state: IGameState): IGameState {
        // Calculate bonus cards' points. For each card remaining in the stack add 5 points plus another 5
        // points for each card.
        const increment = 5;

        const remainingCards = [
            ...state.levelState.cardsState.inStack,
            ...(isGoldLevel(state.levelState) ? state.levelState.cardsState.inPlay : []),
        ].map(cardID => state.levelState.cardsState.cards[cardID]);

        const bonus = (increment / 2) * remainingCards.length * (remainingCards.length + 1);
        const score = state.levelState.score + bonus;
        const stars = state.levelState.stars.filter(n => n <= score).length;

        const playerLevelData = state.playerState.levels[state.levelState.levelType].find(
            levelData => state.levelState.levelNumber === levelData.levelNumber
        );

        const starsGained = playerLevelData.stars ? stars - playerLevelData.stars : stars;

        const firstComplete: boolean = !playerLevelData.stars && stars > 0;

        const coins =
            !playerLevelData.stars && stars > 0
                ? state.levelState.coins[stars - 1]
                : playerLevelData.stars < stars
                ? state.levelState.coins[stars - 1] - state.levelState.coins[playerLevelData.stars - 1]
                : 0;

        const levelCost: number = LevelHelper.getLevelCost(playerLevelData.levelNumber);

        let stackReward = 0;
        if (firstComplete) {
            stackReward += getAllRemainingCardsReward({
                levelCost,
                startFactor: remainingStackRewardStartFactor,
                linearFactor: remainingStackRewardStartLinearFactor,
                cards: remainingCards,
                firstComplete,
            });
        }

        state = {
            ...state,
            levelState: {
                ...state.levelState,
                score: score,
                bonus: bonus,
                status: LevelStatus.COMPLETE,
                completeData: {
                    levelNumber: state.levelState.levelNumber,
                    score,
                    stars,
                    coins,
                    stackReward,
                    firstComplete,
                    starsGained,
                    rewards: [...state.levelState.onCompleteRewards],
                },
            },
        };

        const levelNumber: number = state.levelState.levelNumber;
        if (firstComplete && (levelNumber === 5 || levelNumber === 10 || levelNumber === 20)) {
            FBPixel.track(`Pixel_SOFA_Completelvl_${levelNumber}`);
        }

        const trackingLevel: number = LEVELS_TO_TRACK.find(level => levelNumber === level);
        if (firstComplete && trackingLevel) {
            const trackString = 'levelComplete_' + String(trackingLevel);
            Game.sdk.gameTracking(Object.values(FBTrackingEvent).find(event => event === trackString));
        }

        return state;
    }

    public static isFirstLevelAttempt(state: IGameState, levelNumber: number, levelType = LevelType.Core): boolean {
        const levelData = ProgressStateHelper.getLevelData(
            state,
            state.levelState.levelNumber,
            state.levelState.levelType
        );

        return levelData === undefined || (levelData.attempts ?? 0) <= 1;
    }

    public static setStateTaskData(state: IGameState, taskData: ILevelStateTaskData): IGameState {
        state.levelState.tasks = state.levelState.tasks || [];
        return {
            ...state,
            levelState: {
                ...state.levelState,
                tasks: [...state.levelState.tasks, taskData],
            },
        };
    }

    public static getCoinRewardAmount(state: IGameState): IGameState {
        const levelCost: number = LevelHelper.getLevelCost(state.levelState.levelNumber);

        const coins: number[] = levelCompleteRewardMultiplier.map((multiplier: number) => multiplier * levelCost);
        return {
            ...state,
            levelState: {
                ...state.levelState,
                coins,
            },
        };
    }

    public static getBoosterPrices(state: IGameState): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                boosterPrices: createAnyBoostersRecord(boosterType =>
                    LevelStateHelper.getBoosterPrice(
                        boosterType,
                        state.levelState.levelNumber,
                        state.levelState.boosterUses
                    )
                ),
            },
        };
    }

    public static getBoosterPrice(
        boosterType: AnyBoosterType,
        levelNumber: number,
        boosterUses?: Readonly<ILevelState['boosterUses']>
    ) {
        if (boosterType === BoosterType.PLUS_FIVE && levelNumber <= lastFreePlusFiveBooster) {
            return 0;
        }

        const levelCost = LevelHelper.getLevelCost(levelNumber);
        const multiplier = ArrayHelper.getClamped(
            multiplierPerBoosterType[boosterType],
            boosterUses?.[boosterType] ?? 0
        )!;
        return Math.round(levelCost * multiplier);
    }

    public static ensureBoosterUses(state: IGameState): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                boosterUses: state.levelState.boosterUses ?? createAnyBoostersRecord(() => 0),
            },
        };
    }

    public static useExtraCardBoosters(state: IGameState): IGameState {
        const targets: string[] = [];
        let cardState: ICardsState = CardStateHelper.addNewCards(
            state.levelState.cardsState,
            state.playerState.inventory[BoosterType.PLUS_ONE],
            targets
        );

        state = {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: cardState,
            },
            playerState: {
                ...state.playerState,
                inventory: {
                    ...state.playerState.inventory,
                    [BoosterType.PLUS_ONE]: 0,
                },
            },
        };
        return state;
    }
    public static checkLevelCompleted(state: IGameState): boolean {
        return ProgressStateHelper.getLevelStars(state, state.levelState.levelNumber, state.levelState.levelType) > 0;
    }

    public static queueFTUXTasksIfNeeded(state: IGameState): IGameState {
        // TODO: Use prio to filter correct tasks
        const FTUXtask = FTUX_LEVEL_TASKS.find(ftux => ftux.condition(state));

        if (FTUXtask) {
            state = LevelStateHelper.setStateTaskData(state, {
                task: FTUXtask.taskType,
            });
        }

        return state;
    }

    public static handleLevelComplete(state: IGameState): IGameState {
        // Check for level complete.
        if (isLevelCompletedSuccessfully(state.levelState)) {
            return LevelStateHelper.handleSuccessfulLevelComplete(state);
        }

        return state;
    }

    private static handleSuccessfulLevelComplete(state: IGameState): IGameState {
        track('mScoreBeforeEndSequence', state.levelState.score);
        track('mMovesLeft', state.levelState.cardsState.inStack.length);

        // TODO: Use prio to filter correct tasks
        const FTUXtask = FTUX_LEVEL_TASKS.find(ftux => ftux.condition(state));

        if (FTUXtask) {
            state = {
                ...state,
                playerState: FTUXStateHelper.updateFTUXProgress(state.playerState, [FTUXtask.type]),
            };
        }

        const levelData = ProgressStateHelper.getLevelData(
            state,
            state.levelState.levelNumber,
            state.levelState.levelType
        );
        const firstComplete: boolean = !levelData || !levelData.stars;

        // Calculate end of level totals
        state = LevelStateHelper.levelComplete(state);

        if (state.UIState.popupID) {
            state = {
                ...state,
                UIState: UIStateHelper.hidePopup(state.UIState, state.UIState.popupID, {}),
            };
        }
        return state;
    }

    public static addOnLevelCompleteRewards(state: IGameState, ...rewards: IRewardData[]): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                onCompleteRewards: [...state.levelState.onCompleteRewards, ...rewards],
            },
        };
    }

    public static checkEndLevelInterstitialAd(state: IGameState): IGameState {
        if (canShowInterstitialAd(state)) {
            state = ContextStateHelper.setContext(state, ContextType.DISPLAY_ADVERTISEMENT, {
                g_gameArea: AdvertisementArea.LEVEL_COMPLETE,
            });
        }
        return state;
    }

    public static setLevelPreBoosters(state: IGameState, preBoosters: PreBoosterType[]): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                preBoosters,
            },
        };
    }
}

export function isNearEndGame(state: IGameState) {
    return state.levelState.cardsState.inStack.length <= 3 && state.levelState.cardsState.inPlay.length > 0;
}

export const getAllRemainingCardsReward = (props: {
    cards: ICardStateData[];
    levelCost: number;
    startFactor: number;
    linearFactor: number;
    firstComplete: boolean;
}) => {
    let result = 0;

    for (let i = 0; i < props.cards.length; i += 1) {
        result += getSingleRemainingCardReward({
            levelCost: props.levelCost,
            startFactor: props.startFactor,
            linearFactor: props.linearFactor,
            card: props.cards[i],
            cardIndex: i,
            firstComplete: props.firstComplete,
        });
    }

    return result;
};

const multiplierForRemainingCard: { [key in CardType]: ((card: ICardStateData & { type: key }) => number) | null } = {
    [CardType.boom]: null,
    [CardType.zap]: null,
    [CardType.lock]: null,
    [CardType.key]: null,
    [CardType.plus_three]: null,
    [CardType.value]: null,
    [CardType.rock]: null,
    [CardType.scissors]: null,
    [CardType.tornado]: null,
    [CardType.gold]: null,
    [CardType.joker]: null,
    [CardType.joker_pre_booster]: card => (card.madeByBooster ? 0.5 : 0.5),
    [CardType.canister_pre_booster]: () => 1.5,
    [CardType.plinko]: null,
};

export const getSingleRemainingCardReward = (props: {
    card: ICardStateData;
    cardIndex: number;
    levelCost: number;
    startFactor: number;
    linearFactor: number;
    firstComplete: boolean;
}): number => {
    const { card, cardIndex, levelCost, startFactor, linearFactor } = props;
    const getMultiplier = multiplierForRemainingCard[card.type];
    let result: number;

    if (getMultiplier) {
        // @ts-ignore
        result = levelCost * getMultiplier(card);
    } else if (props.firstComplete) {
        const startReward = levelCost * startFactor;
        const linearReward = levelCost * linearFactor;

        result = Math.ceil(startReward + linearReward * cardIndex);
    } else {
        result = 5 * (cardIndex + 1);
    }

    return result;
};

export const isGoldLevel = (state: Immutable<ILevelState>) => {
    return state.winCriteria.some(data => data.type === WinCriteriaType.CLEAR_GOLD);
};

export const isDynamicAILevel = (state: IGameState, levelType: LevelType = LevelType.Core): boolean => {
    switch (state.levelState.levelType) {
        case LevelType.Core:
            return state.levelState.levelNumber >= LevelHelperSettings.dynamicStartLevel;
        default:
            unexpectedValue(state.levelState.levelType, false);
            return false;
    }
};
export const canShowInterstitialAd = (state: IGameState): boolean => {
    return (
        state.levelState.levelNumber >= INTERSTITIAL_START_LEVEL &&
        state.levelState.levelNumber % INTERSTITIAL_FREQUENCY === 0 &&
        !state.playerState.purchases
    );
};

export const isLevelCompletedSuccessfully = (state: Immutable<ILevelState>): boolean => {
    const remainingGoldCards = state.cardsState.inPlay.filter((cardID: string) => {
        return state.cardsState.cards[cardID].type === CardType.gold;
    });
    return state.cardsState.inPlay.length === 0 || (isGoldLevel(state) && remainingGoldCards.length === 0);
};

export function getValidLevelFailReason(state: IGameState): LevelFailReason | void {
    if (hasTNTModifierExploded(state)) {
        return LevelFailReason.TNT_EXPLODED;
    }
}

export function hasTNTModifierExploded(state: IGameState): boolean {
    const tntModifiersExploded = state.levelState.cardsState.inPlay.filter((cardID: string) => {
        const tntModifiers = CardStateHelper.getModifiersOfType(
            state.levelState.cardsState,
            cardID,
            CardModifierType.tnt
        );
        return tntModifiers.some(mod => mod.properties.timer <= 0);
    });

    return tntModifiersExploded.length > 0;
}

export const isLevelFailed = (state: IGameState): boolean => {
    return Boolean(getValidLevelFailReason(state));
};

export const getHardLevelBGKey = (_levelNumber: number, worldNumber?: number): string => {
    const fieldData = WorldDataHelper.getFieldByLevelNumber(_levelNumber, worldNumber);

    if (!fieldData) return;

    return getFieldUnlockShareKey(fieldData.id);
};
